import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, ApiError } from './api'
import { fetchMe, logout as postLogout } from './session'
import type { Session, SessionKind } from './session'

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session }

type SessionContextValue = {
  state: SessionState
  /** kind ที่ล็อกอินล่าสุดในแท็บนี้ — ใช้เลือกว่าจะเด้งไปหน้า login ทางไหน */
  lastKind: SessionKind | null
  /** ข้อความค้างไว้ให้หน้า login แสดง เช่น "เซสชันหมดอายุ" */
  notice: string | null
  clearNotice: () => void
  signIn: (session: Session) => void
  signOut: () => Promise<void>
  /** โหลด /api/auth/me ใหม่ — ใช้หลังเปลี่ยนรหัสผ่านเพื่อล้างธง mustChangePassword */
  refresh: () => Promise<Session | null>
  /**
   * เรียก backend แบบรู้จัก session — เจอ 401 แล้วเคลียร์ session ให้ที่นี่ที่เดียว
   * หน้าจอที่ยิง API ควรใช้ตัวนี้แทน api() ตรง ๆ ยกเว้น endpoint login
   */
  call: typeof api
}

const SessionContext = createContext<SessionContextValue | null>(null)

const EXPIRED_MESSAGE = 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง'

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' })
  const [lastKind, setLastKind] = useState<SessionKind | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const signIn = useCallback((session: Session) => {
    setState({ status: 'signed-in', session })
    setLastKind(session.kind)
    setNotice(null)
  }, [])

  const clear = useCallback((reason: string | null) => {
    setState({ status: 'signed-out' })
    setNotice(reason)
  }, [])

  const refresh = useCallback(async (): Promise<Session | null> => {
    const session = await fetchMe()
    if (session) signIn(session)
    else clear(EXPIRED_MESSAGE)
    return session
  }, [clear, signIn])

  useEffect(() => {
    let cancelled = false

    fetchMe()
      .then((session) => {
        if (cancelled) return
        if (session) signIn(session)
        // ตอนเปิดแอปครั้งแรก "ไม่ได้ล็อกอิน" เป็นเรื่องปกติ ห้ามตั้ง notice
        // ไม่งั้นหน้า login จะขึ้นว่า session หมดอายุให้คนที่ยังไม่เคยล็อกอินเลย
        else setState({ status: 'signed-out' })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // ต่อ backend ไม่ได้เลย — ยังต้องปล่อยให้เข้าหน้า login ได้ แต่บอกสาเหตุไว้
        // ไม่งั้นผู้ใช้จะเห็นแค่ฟอร์มที่กดแล้วเงียบ ๆ
        clear(`ต่อ backend ไม่ได้ — ${err instanceof Error ? err.message : String(err)}`)
      })

    return () => {
      cancelled = true
    }
  }, [clear, signIn])

  const signOut = useCallback(async () => {
    // ถ้า backend ตอบ error (เช่น session ตายไปแล้ว) ก็ยังต้องเคลียร์ฝั่งหน้าเว็บ
    // ไม่งั้นผู้ใช้กดออกจากระบบแล้วค้างอยู่หน้าเดิม
    await postLogout().catch(() => {})
    clear(null)
  }, [clear])

  const call = useCallback<typeof api>(
    async (path, init) => {
      try {
        return await api(path, init)
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) clear(EXPIRED_MESSAGE)
        throw err
      }
    },
    [clear],
  )

  const value = useMemo<SessionContextValue>(
    () => ({
      state,
      lastKind,
      notice,
      clearNotice: () => setNotice(null),
      signIn,
      signOut,
      refresh,
      call,
    }),
    [state, lastKind, notice, signIn, signOut, refresh, call],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession ต้องอยู่ใต้ <SessionProvider>')
  return ctx
}

/**
 * สำหรับหน้าที่อยู่ใต้ Shell — ตรงนั้นผ่าน guard มาแล้วจึงการันตีว่าล็อกอินอยู่
 * ให้ throw ดัง ๆ ตอน dev ดีกว่าปล่อยให้ทุกหน้าเขียน `session?.` ลามไปทั้งไฟล์
 */
export function useAuthedSession(): Session {
  const { state } = useSession()
  if (state.status !== 'signed-in') throw new Error('หน้านี้ต้องอยู่ใต้ guard ที่ล็อกอินแล้ว')
  return state.session
}
